export type CampId = 'blue' | 'gromp' | 'wolves' | 'raptors' | 'red' | 'krugs' | 'scuttle';

export type Camp = {
  id: CampId;
  name: string;
  initial: number;     // spawn time (s)
  respawn: number;     // respawn (s)
  xpRange?: [number, number];    // linear scale by monster level
  gold?: number;                 // fixed
  goldRange?: [number, number];  // linear scale (scuttle)
  firstSpawnXPModifier?: number; // scuttle -80% XP first pair
  group: 'blue' | 'red' | 'river';
};

/** Parse mm:ss (or seconds as number) → seconds */
export function toSec(t: string | number): number {
  if (typeof t === 'number') return t;
  if (!t) return 0;
  const [m, s] = String(t).split(':').map(Number);
  return (m || 0) * 60 + (s || 0);
}

/** Format seconds → m:ss */
export function fmt(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/** Champion XP to next level (SR) */
export const CHAMP_XP_TO_NEXT: number[] = [
  0,   // dummy for index 0
  280, // to 2
  380, // to 3
  480, // to 4
  580, // to 5
  680, // to 6
  780, // to 7
  880, // to 8
  980, // to 9
  1080,// to 10
  1180,// to 11
  1280,// to 12
  1380,// to 13
  1480,// to 14
  1580,// to 15
  1680,// to 16
  1780,// to 17
  1880,// to 18
];

export const CHAMP_XP_CUM: number[] = (() => {
  const arr = [0];
  let sum = 0;
  for (let lvl = 1; lvl <= 18; lvl++) {
    sum += CHAMP_XP_TO_NEXT[lvl] || 0;
    arr[lvl] = sum;
  }
  return arr;
})();

export function champLevelFromXP(totalXP: number): { level: number; into: number; toNext: number } {
  if (totalXP <= 0) return { level: 1, into: 0, toNext: CHAMP_XP_TO_NEXT[1] };
  for (let lvl = 1; lvl < 18; lvl++) {
    if (totalXP < CHAMP_XP_CUM[lvl]) {
      const prev = CHAMP_XP_CUM[lvl - 1] || 0;
      const toNext = CHAMP_XP_TO_NEXT[lvl] || 0;
      return { level: lvl, into: totalXP - prev, toNext };
    }
  }
  return { level: 18, into: 0, toNext: 0 };
}

/** Linear scale a [min,max] by monster level 1..maxLevel */
export function lerpByLevel([min, max]: [number, number], monsterLevel: number, maxLevel = 18): number {
  const L = Math.max(1, Math.min(maxLevel, monsterLevel));
  if (maxLevel <= 1) return min;
  return min + ((max - min) * (L - 1)) / (maxLevel - 1);
}

/** Wolves per-level override (your reference):
 * Greater wolf: L1 50, L2 50, L3 62.5, L4 65, then +2.5/level until level 8
 * Small wolves approximated: 15→22.5 by level 8 (linear)
 * Returns total camp XP (greater + 2*small)
 */
export function wolvesXPOverride(monsterLevel: number): number {
  const lvl = Math.min(Math.max(1, monsterLevel), 8);
  // greater
  let greater = 50;
  if (lvl === 3) greater = 62.5;
  else if (lvl >= 4) greater = 65 + (lvl - 4) * 2.5;
  // smalls linear 15→22.5 by L8
  const small = 15 + ((22.5 - 15) * (lvl - 1)) / (8 - 1);
  return greater + 2 * small;
}

/** Camp data. XP ranges approximate wiki scaling; Wolves can use the override above. */
export const CAMPS: Record<CampId, Camp> = {
  blue: {
    id: 'blue',
    name: 'Blue Sentinel',
    initial: toSec('1:30'),
    respawn: toSec('5:00'),
    xpRange: [95, 142.5],
    gold: 90,
    group: 'blue',
  },
  gromp: {
    id: 'gromp',
    name: 'Gromp',
    initial: toSec('1:30'),
    respawn: toSec('2:15'),
    xpRange: [120, 180],
    gold: 80,
    group: 'blue',
  },
  wolves: {
    id: 'wolves',
    name: 'Wolves (camp total)',
    initial: toSec('1:30'),
    respawn: toSec('2:15'),
    // Greater (50–75) + 2x Small (15–22.5) => ~80–120
    xpRange: [80, 120],
    gold: 81, // 55 + 13 + 13
    group: 'blue',
  },
  raptors: {
    id: 'raptors',
    name: 'Raptors (camp total)',
    initial: toSec('1:30'),
    respawn: toSec('2:15'),
    // Crimson (20–30) + 5x Small (10–15) => 70–105
    xpRange: [70, 105],
    gold: 75, // 35 + 5*8
    group: 'red',
  },
  red: {
    id: 'red',
    name: 'Red Brambleback',
    initial: toSec('1:30'),
    respawn: toSec('5:00'),
    xpRange: [95, 142.5],
    gold: 90,
    group: 'red',
  },
  krugs: {
    id: 'krugs',
    name: 'Krugs (camp total)',
    initial: toSec('1:30'),
    respawn: toSec('2:15'),
    // Ancient (15–22.5) + Krug (10–15) + 4x Mini (16–24) => ~89–133.5
    xpRange: [89, 133.5],
    gold: 81, // 15 + 10 + 4*14
    group: 'red',
  },
  scuttle: {
    id: 'scuttle',
    name: 'Rift Scuttler',
    initial: toSec('3:30'),
    respawn: toSec('2:30'),
    xpRange: [100, 150],
    goldRange: [55, 121],
    firstSpawnXPModifier: 0.2, // −80% XP on the first pair
    group: 'river',
  },
};

/** Wave configuration - based on WaveController.ts logic */
export const WAVE_CONFIG = {
  firstSpawn: toSec('1:05'), // Minions spawn at nexus
  arrivalTime: toSec('1:30'), // First wave arrives at lane (30s travel)
  spawnInterval: 30, // New wave every 30 seconds
  
  // Minion gold values (per minion)
  minionGold: {
    melee: 21,      // 3 melee minions per wave
    caster: 14,     // 3 caster minions per wave
    cannon: 60,     // 1 cannon minion every 3rd wave (starts wave 3)
  },
  
  // Minion XP values (approximate)
  minionXP: {
    melee: 29,
    caster: 29,
    cannon: 92,
  }
};

/**
 * Calculate wave gold for a specific wave number
 */
export function getWaveGold(waveNumber: number): { total: number; melee: number; caster: number; cannon: number } {
  const hasCannonMinion = waveNumber >= 3 && waveNumber % 3 === 0;
  
  const melee = 3 * WAVE_CONFIG.minionGold.melee;
  const caster = 3 * WAVE_CONFIG.minionGold.caster;
  const cannon = hasCannonMinion ? WAVE_CONFIG.minionGold.cannon : 0;
  
  return {
    total: melee + caster + cannon,
    melee,
    caster,
    cannon
  };
}

/**
 * Calculate wave XP for a specific wave number
 */
export function getWaveXP(waveNumber: number): { total: number; melee: number; caster: number; cannon: number } {
  const hasCannonMinion = waveNumber >= 3 && waveNumber % 3 === 0;
  
  const melee = 3 * WAVE_CONFIG.minionXP.melee;
  const caster = 3 * WAVE_CONFIG.minionXP.caster;
  const cannon = hasCannonMinion ? WAVE_CONFIG.minionXP.cannon : 0;
  
  return {
    total: melee + caster + cannon,
    melee,
    caster,
    cannon
  };
}

/**
 * Get wave timing (when it spawns, arrives, and should be cleared)
 */
export function getWaveTiming(waveNumber: number): { spawn: number; arrival: number; expectedClear: number } {
  const spawn = WAVE_CONFIG.firstSpawn + (waveNumber - 1) * WAVE_CONFIG.spawnInterval;
  const arrival = spawn + 30; // 30s travel time
  const expectedClear = arrival + 30; // Expect to clear within 30s
  
  return { spawn, arrival, expectedClear };
}